import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  LinearProgress,
  Skeleton,
  useTheme,
  alpha,
  Tooltip,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  IconButton,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  Description as InvoiceIcon,
  Work as PayrollIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { collection, query, where, getDocs } from "firebase/firestore";
import { auth, db } from "../firebase";
import { formatCurrency } from "../utils/currency";
import { useTrial } from "../contexts/TrialContext";
import UpgradePrompt from "../components/UpgradePrompt";
import { motion, AnimatePresence } from "framer-motion";

export default function ProjectProfitability() {
  const theme = useTheme();
  const { plan, isTrialExpired } = useTrial();
  const isPro = plan === "pro" || plan === "proplus";

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedProject, setExpandedProject] = useState(null);
  const [viewMode, setViewMode] = useState("all"); // all | profitable | loss

  useEffect(() => {
    if (isPro || !isTrialExpired) {
      fetchProjectProfitability();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchProjectProfitability = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      // Fetch all projects
      const projectsSnap = await getDocs(
        query(collection(db, "projects"), where("uid", "==", user.uid)),
      );
      const projectList = projectsSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Fetch all invoices
      const invoicesSnap = await getDocs(
        query(collection(db, "invoices"), where("userId", "==", user.uid)),
      );
      const invoicesByProject = {};
      invoicesSnap.docs.forEach((doc) => {
        const d = doc.data();
        if (d.projectId) {
          if (!invoicesByProject[d.projectId])
            invoicesByProject[d.projectId] = [];
          invoicesByProject[d.projectId].push(d);
        }
      });

      // Fetch all receipts
      const receiptsSnap = await getDocs(
        query(collection(db, "receipts"), where("uid", "==", user.uid)),
      );
      const receiptsByProject = {};
      receiptsSnap.docs.forEach((doc) => {
        const d = doc.data();
        if (d.projectId) {
          if (!receiptsByProject[d.projectId])
            receiptsByProject[d.projectId] = [];
          receiptsByProject[d.projectId].push(d);
        }
      });

      // Fetch all payroll
      const payrollSnap = await getDocs(
        query(collection(db, "payroll"), where("uid", "==", user.uid)),
      );
      const payrollByProject = {};
      payrollSnap.docs.forEach((doc) => {
        const d = doc.data();
        if (d.projectId) {
          if (!payrollByProject[d.projectId])
            payrollByProject[d.projectId] = [];
          payrollByProject[d.projectId].push(d);
        }
      });

      // Build profitability data per project
      const projectsWithData = projectList.map((project) => {
        const invoices = invoicesByProject[project.id] || [];
        const receipts = receiptsByProject[project.id] || [];
        const payroll = payrollByProject[project.id] || [];

        const totalInvoiced = invoices.reduce(
          (sum, inv) => sum + (inv.totalAmount || 0),
          0,
        );
        const totalPaid = invoices
          .filter((inv) => inv.status === "paid")
          .reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);
        const totalUnpaid = totalInvoiced - totalPaid;
        const totalReceipts = receipts.reduce(
          (sum, r) => sum + (r.amount || 0),
          0,
        );
        const totalPayroll = payroll.reduce(
          (sum, p) => sum + (p.amount || 0),
          0,
        );
        const totalCosts = totalReceipts + totalPayroll;
        const profit = totalPaid - totalCosts;
        const margin =
          totalPaid > 0 ? ((profit / totalPaid) * 100).toFixed(1) : 0;

        return {
          ...project,
          totalInvoiced,
          totalPaid,
          totalUnpaid,
          totalReceipts,
          totalPayroll,
          totalCosts,
          profit,
          margin: parseFloat(margin),
          invoiceCount: invoices.length,
          receiptCount: receipts.length,
          payrollCount: payroll.length,
        };
      });

      // Sort by profit descending
      projectsWithData.sort((a, b) => b.profit - a.profit);
      setProjects(projectsWithData);
    } catch (err) {
      console.error("Error fetching profitability:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = useMemo(() => {
    if (viewMode === "profitable") return projects.filter((p) => p.profit >= 0);
    if (viewMode === "loss") return projects.filter((p) => p.profit < 0);
    return projects;
  }, [projects, viewMode]);

  // Summary stats
  const totals = useMemo(() => {
    return projects.reduce(
      (acc, p) => ({
        revenue: acc.revenue + p.totalPaid,
        costs: acc.costs + p.totalCosts,
        profit: acc.profit + p.profit,
        unpaid: acc.unpaid + p.totalUnpaid,
      }),
      { revenue: 0, costs: 0, profit: 0, unpaid: 0 },
    );
  }, [projects]);

  const maxRevenue = useMemo(
    () => Math.max(...projects.map((p) => p.totalPaid || 1), 1),
    [projects],
  );

  // Pro-only gate (placed after all hooks)
  if (!isPro && isTrialExpired) {
    return (
      <Box maxWidth="800px" mx="auto" mt={8} px={3}>
        <UpgradePrompt
          source="project-profitability"
          title="📊 Project Profitability is a Pro feature"
          subtitle="See exactly which jobs are making money — and which are costing you. Upgrade to unlock real-time profit tracking per project."
        />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box maxWidth="1000px" mx="auto" p={3}>
        <Skeleton variant="text" width={300} height={40} />
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid key={i} size={{ xs: 6, md: 3 }}>
              <Skeleton variant="rounded" height={100} />
            </Grid>
          ))}
        </Grid>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rounded" height={120} sx={{ mt: 2 }} />
        ))}
      </Box>
    );
  }

  return (
    <Box maxWidth="1000px" mx="auto">
      {/* Header */}
      <Box
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        mb={3}
        gap={2}
      >
        <Box>
          <Typography variant="h5" fontWeight={800} letterSpacing={-0.5}>
            Project Profitability
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Real-time profit & loss across all your jobs
          </Typography>
        </Box>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, val) => val && setViewMode(val)}
          size="small"
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="profitable">Profitable</ToggleButton>
          <ToggleButton value="loss">At Loss</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Paper
            sx={{
              p: 2.5,
              borderRadius: 3,
              borderLeft: "4px solid",
              borderColor: "success.main",
            }}
          >
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ fontSize: "0.65rem", fontWeight: 700 }}
            >
              Revenue Collected
            </Typography>
            <Typography variant="h5" fontWeight={800} color="success.main">
              {formatCurrency(totals.revenue)}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Paper
            sx={{
              p: 2.5,
              borderRadius: 3,
              borderLeft: "4px solid",
              borderColor: "error.main",
            }}
          >
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ fontSize: "0.65rem", fontWeight: 700 }}
            >
              Total Costs
            </Typography>
            <Typography variant="h5" fontWeight={800} color="error.main">
              {formatCurrency(totals.costs)}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Paper
            sx={{
              p: 2.5,
              borderRadius: 3,
              borderLeft: "4px solid",
              borderColor: totals.profit >= 0 ? "success.main" : "error.main",
            }}
          >
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ fontSize: "0.65rem", fontWeight: 700 }}
            >
              Net Profit
            </Typography>
            <Typography
              variant="h5"
              fontWeight={800}
              color={totals.profit >= 0 ? "success.main" : "error.main"}
            >
              {formatCurrency(totals.profit)}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Paper
            sx={{
              p: 2.5,
              borderRadius: 3,
              borderLeft: "4px solid",
              borderColor: "warning.main",
            }}
          >
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ fontSize: "0.65rem", fontWeight: 700 }}
            >
              Awaiting Payment
            </Typography>
            <Typography variant="h5" fontWeight={800} color="warning.main">
              {formatCurrency(totals.unpaid)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Empty State */}
      {filteredProjects.length === 0 && (
        <Paper
          sx={{
            p: 6,
            borderRadius: 3,
            textAlign: "center",
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {projects.length === 0
              ? "No projects yet"
              : "No projects match this filter"}
          </Typography>
          <Typography variant="body2" color="text.disabled">
            {projects.length === 0
              ? "Create a project and tag your invoices & receipts to it to see profitability."
              : "Try switching the filter above."}
          </Typography>
        </Paper>
      )}

      {/* Project Cards */}
      <AnimatePresence>
        {filteredProjects.map((project, index) => {
          const isExpanded = expandedProject === project.id;
          const isProfitable = project.profit >= 0;
          const revenueBarWidth = (project.totalPaid / maxRevenue) * 100;
          const costBarWidth = (project.totalCosts / maxRevenue) * 100;

          return (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <Paper
                sx={{
                  p: 0,
                  mb: 2,
                  borderRadius: 3,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: isExpanded
                    ? isProfitable
                      ? "success.main"
                      : "error.main"
                    : "divider",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: isProfitable ? "success.main" : "error.main",
                    boxShadow: `0 4px 20px ${alpha(
                      isProfitable
                        ? theme.palette.success.main
                        : theme.palette.error.main,
                      0.1,
                    )}`,
                  },
                }}
              >
                {/* Main Row */}
                <Box
                  sx={{
                    p: 2.5,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                  onClick={() =>
                    setExpandedProject(isExpanded ? null : project.id)
                  }
                >
                  {/* Profit indicator */}
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: alpha(
                        isProfitable
                          ? theme.palette.success.main
                          : theme.palette.error.main,
                        0.1,
                      ),
                      flexShrink: 0,
                    }}
                  >
                    {isProfitable ? (
                      <TrendingUpIcon color="success" />
                    ) : (
                      <TrendingDownIcon color="error" />
                    )}
                  </Box>

                  {/* Project name + stats */}
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <Typography variant="subtitle1" fontWeight={700} noWrap>
                        {project.name}
                      </Typography>
                      <Chip
                        label={
                          isProfitable
                            ? `+${project.margin}%`
                            : `${project.margin}%`
                        }
                        size="small"
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.7rem",
                          bgcolor: alpha(
                            isProfitable
                              ? theme.palette.success.main
                              : theme.palette.error.main,
                            0.1,
                          ),
                          color: isProfitable ? "success.main" : "error.main",
                        }}
                      />
                      {project.status && (
                        <Chip
                          label={project.status}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: "0.65rem" }}
                        />
                      )}
                    </Box>

                    {/* Revenue & Cost bars */}
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Tooltip
                          title={`Revenue: ${formatCurrency(project.totalPaid)}`}
                        >
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(revenueBarWidth, 100)}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              bgcolor: alpha(theme.palette.success.main, 0.08),
                              "& .MuiLinearProgress-bar": {
                                bgcolor: "success.main",
                                borderRadius: 3,
                              },
                            }}
                          />
                        </Tooltip>
                        <Tooltip
                          title={`Costs: ${formatCurrency(project.totalCosts)}`}
                        >
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(costBarWidth, 100)}
                            sx={{
                              mt: 0.5,
                              height: 6,
                              borderRadius: 3,
                              bgcolor: alpha(theme.palette.error.main, 0.08),
                              "& .MuiLinearProgress-bar": {
                                bgcolor: "error.main",
                                borderRadius: 3,
                              },
                            }}
                          />
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>

                  {/* Profit amount */}
                  <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                    <Typography
                      variant="h6"
                      fontWeight={800}
                      color={isProfitable ? "success.main" : "error.main"}
                      sx={{ letterSpacing: -0.5 }}
                    >
                      {isProfitable ? "+" : ""}
                      {formatCurrency(project.profit)}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      net profit
                    </Typography>
                  </Box>

                  <IconButton size="small">
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                {/* Expanded Details */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      style={{ overflow: "hidden" }}
                    >
                      <Divider />
                      <Box
                        sx={{
                          p: 2.5,
                          bgcolor: alpha(theme.palette.background.default, 0.5),
                        }}
                      >
                        <Grid container spacing={2}>
                          {/* Revenue */}
                          <Grid size={{ xs: 6, md: 3 }}>
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={1}
                              mb={0.5}
                            >
                              <InvoiceIcon
                                sx={{
                                  fontSize: 18,
                                  color: "success.main",
                                }}
                              />
                              <Typography
                                variant="caption"
                                fontWeight={700}
                                color="text.secondary"
                              >
                                INVOICED
                              </Typography>
                            </Box>
                            <Typography fontWeight={700}>
                              {formatCurrency(project.totalInvoiced)}
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                              {project.invoiceCount} invoice
                              {project.invoiceCount !== 1 ? "s" : ""}
                            </Typography>
                          </Grid>

                          {/* Paid */}
                          <Grid size={{ xs: 6, md: 3 }}>
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={1}
                              mb={0.5}
                            >
                              <AccountBalanceIcon
                                sx={{
                                  fontSize: 18,
                                  color: "success.main",
                                }}
                              />
                              <Typography
                                variant="caption"
                                fontWeight={700}
                                color="text.secondary"
                              >
                                RECEIVED
                              </Typography>
                            </Box>
                            <Typography fontWeight={700} color="success.main">
                              {formatCurrency(project.totalPaid)}
                            </Typography>
                            {project.totalUnpaid > 0 && (
                              <Typography
                                variant="caption"
                                color="warning.main"
                              >
                                {formatCurrency(project.totalUnpaid)} unpaid
                              </Typography>
                            )}
                          </Grid>

                          {/* Expenses */}
                          <Grid size={{ xs: 6, md: 3 }}>
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={1}
                              mb={0.5}
                            >
                              <ReceiptIcon
                                sx={{ fontSize: 18, color: "error.main" }}
                              />
                              <Typography
                                variant="caption"
                                fontWeight={700}
                                color="text.secondary"
                              >
                                EXPENSES
                              </Typography>
                            </Box>
                            <Typography fontWeight={700} color="error.main">
                              {formatCurrency(project.totalReceipts)}
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                              {project.receiptCount} receipt
                              {project.receiptCount !== 1 ? "s" : ""}
                            </Typography>
                          </Grid>

                          {/* Payroll */}
                          <Grid size={{ xs: 6, md: 3 }}>
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={1}
                              mb={0.5}
                            >
                              <PayrollIcon
                                sx={{ fontSize: 18, color: "error.main" }}
                              />
                              <Typography
                                variant="caption"
                                fontWeight={700}
                                color="text.secondary"
                              >
                                PAYROLL
                              </Typography>
                            </Box>
                            <Typography fontWeight={700} color="error.main">
                              {formatCurrency(project.totalPayroll)}
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                              {project.payrollCount} entr
                              {project.payrollCount !== 1 ? "ies" : "y"}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Paper>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </Box>
  );
}
